import {
  createContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  getPrivacyPreferencesSnapshot,
  hydratePrivacyPreferences,
  setAnalyticsConsent,
  setConsentChoices,
  setMarketingConsent,
  subscribePrivacyPreferences,
  type AnalyticsConsent,
  type ConsentChoices,
  type ConsentDecisionSource,
  type ConsentState,
  type PrivacyPreferences,
} from "../lib/privacy-preferences";

export interface PrivacyPreferencesContextValue {
  isHydrated: boolean;
  preferences: PrivacyPreferences;
  analyticsEnabled: boolean;
  marketingEnabled: boolean;
  setAnalyticsConsent: (
    consent: Exclude<AnalyticsConsent, "unknown">,
    source: ConsentDecisionSource,
  ) => Promise<PrivacyPreferences>;
  setMarketingConsent: (
    consent: Exclude<ConsentState, "unknown">,
    source: ConsentDecisionSource,
  ) => Promise<PrivacyPreferences>;
  setConsentChoices: (
    choices: ConsentChoices,
    source: ConsentDecisionSource,
  ) => Promise<PrivacyPreferences>;
}

export const PrivacyPreferencesContext =
  createContext<PrivacyPreferencesContextValue | null>(null);

export function PrivacyPreferencesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const snapshot = useSyncExternalStore(
    subscribePrivacyPreferences,
    getPrivacyPreferencesSnapshot,
    getPrivacyPreferencesSnapshot,
  );

  useEffect(() => {
    void hydratePrivacyPreferences();
  }, []);

  const value = useMemo<PrivacyPreferencesContextValue>(
    () => ({
      isHydrated: snapshot.isHydrated,
      preferences: snapshot.preferences,
      analyticsEnabled:
        snapshot.isHydrated && snapshot.preferences.analytics === "granted",
      marketingEnabled:
        snapshot.isHydrated && snapshot.preferences.marketing === "granted",
      setAnalyticsConsent,
      setMarketingConsent,
      setConsentChoices,
    }),
    [snapshot],
  );

  return (
    <PrivacyPreferencesContext.Provider value={value}>
      {children}
    </PrivacyPreferencesContext.Provider>
  );
}
